import React, { useEffect, useState } from "react";
import { doc, setDoc } from "firebase/firestore";
import { db } from "@/config/firebase";
import { Button } from "@/components/ui/button";

// Shared live state lives in prediction_sessions.challengeLive (host writes, stage reads).
const write = (sessionId, patch) => setDoc(doc(db, "prediction_sessions", sessionId), { challengeLive: patch }, { merge: true });

export const useCountdown = (endsAt) => {
  const [left, setLeft] = useState(null);
  useEffect(() => {
    if (!endsAt) { setLeft(null); return; }
    const end = typeof endsAt === 'number' ? endsAt : (endsAt?.toMillis?.() || new Date(endsAt).getTime());
    const t = setInterval(() => setLeft(Math.max(0, Math.round((end - Date.now()) / 1000))), 250);
    return () => clearInterval(t);
  }, [endsAt]);
  return left;
};

// HOST SIDE ------------------------------------------------------------------
export const ChallengeLiveTools = ({ sessionId, round, live = {}, getUserName }) => {
  const type = round.interactionType || 'NONE';
  const timer = round.timerSeconds;
  const left = useCountdown(live.timerEndsAt);
  const isLive = live.roundId === round.id ? live : {};
  const P = (patch) => write(sessionId, { roundId: round.id, ...isLive, ...patch });

  const startTimer = (secs) => P({ timerEndsAt: Date.now() + secs * 1000, timerStartedAt: Date.now(), stoppedAt: null });
  const stopTiming = () => P({ stoppedAt: Date.now(), timerEndsAt: null });

  const elapsed = isLive.stoppedAt && isLive.timerStartedAt ? (isLive.stoppedAt - isLive.timerStartedAt) / 1000 : null;
  const inWindow = elapsed != null && round.targetSeconds != null && Math.abs(elapsed - round.targetSeconds) <= (round.toleranceSeconds ?? 0);

  const valid = isLive.valid || 0, invalid = isLive.invalid || 0;
  const quizIdx = isLive.quizIndex || 0, quizCorrect = isLive.quizCorrect || 0;
  const media = round.media || [];
  const answers = isLive.answers || [];
  const secs = round.secondaryPlayerNames || [];

  return (
    <div className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-3" data-testid="challenge-live-tools">
      {/* Timer for any challenge with timerSeconds */}
      {timer && type !== 'TIMING_CHALLENGE' && (
        <div className="flex items-center gap-2">
          <Button data-testid="live-timer-start" size="sm" onClick={() => startTimer(timer)} className="bg-amber-600 hover:bg-amber-700">⏱ TIMER {timer}s START</Button>
          {left != null && <span className={`text-2xl font-mono font-bold ${left === 0 ? 'text-red-400' : 'text-amber-300'}`} data-testid="live-timer-left">{left}s</span>}
          <Button size="sm" variant="outline" onClick={() => P({ timerEndsAt: null })} className="border-white/20">Reset</Button>
        </div>
      )}

      {type === 'TIMING_CHALLENGE' && (
        <div className="space-y-2">
          <p className="text-xs text-rose-200/60">Ziel {round.targetSeconds}s ± {round.toleranceSeconds ?? 0}s {round.timerSeconds ? `· Gesamtzeit ${round.timerSeconds}s` : ''}</p>
          <div className="flex items-center gap-2">
            <Button data-testid="timing-start" size="sm" onClick={() => P({ timerStartedAt: Date.now(), stoppedAt: null, timerEndsAt: round.timerSeconds ? Date.now() + round.timerSeconds * 1000 : null })} className="bg-green-600 hover:bg-green-700">
              {round.configSnapshot?.settings?.attemptLabel || 'START'}
            </Button>
            <Button data-testid="timing-stop" size="sm" onClick={stopTiming} disabled={!isLive.timerStartedAt || isLive.stoppedAt} className="bg-red-600 hover:bg-red-700">STOP</Button>
            {elapsed != null && (
              <span data-testid="timing-result" className={`text-xl font-mono font-bold ${inWindow ? 'text-green-400' : 'text-red-400'}`}>
                {elapsed.toFixed(2)}s {round.targetSeconds != null && (inWindow ? '✅ im Fenster' : '❌')}
              </span>
            )}
          </div>
        </div>
      )}

      {type === 'HOST_COUNTER' && (
        <div className="flex items-center gap-2">
          <span className="text-2xl font-black text-amber-300" data-testid="counter-display">{valid} / {round.targetCount ?? '?'}</span>
          <Button data-testid="counter-valid" size="sm" onClick={() => P({ valid: valid + 1 })} className="bg-green-600 hover:bg-green-700">✅ gültig</Button>
          <Button data-testid="counter-invalid" size="sm" onClick={() => P({ invalid: invalid + 1 })} className="bg-red-600/60 hover:bg-red-600">❌ ungültig ({invalid})</Button>
          <Button size="sm" variant="outline" onClick={() => P({ valid: 0, invalid: 0 })} className="border-white/20">Reset</Button>
        </div>
      )}

      {type === 'IMAGE_QUIZ' && (
        <div className="space-y-2">
          <p className="text-sm">Bild {Math.min(quizIdx + 1, media.length)} / {media.length} · richtig: <b className="text-green-400" data-testid="quiz-correct">{quizCorrect}</b> / Ziel {round.targetCount ?? '?'}</p>
          {quizIdx < media.length ? (
            <div className="flex items-center gap-2">
              <span className="text-xs text-rose-200/60 flex-1 truncate">Lösung: <b className="text-amber-300">{media[quizIdx]?.correctAnswer}</b> ({(media[quizIdx]?.acceptedAnswers || []).join(', ')})</span>
              <Button data-testid="quiz-right" size="sm" onClick={() => P({ quizIndex: quizIdx + 1, quizCorrect: quizCorrect + 1 })} className="bg-green-600 hover:bg-green-700">RICHTIG</Button>
              <Button data-testid="quiz-wrong" size="sm" onClick={() => P({ quizIndex: quizIdx + 1 })} className="bg-red-600/60 hover:bg-red-600">FALSCH</Button>
            </div>
          ) : (
            <p data-testid="quiz-final" className={`font-bold ${quizCorrect >= (round.targetCount ?? 0) ? 'text-green-400' : 'text-red-400'}`}>
              Ergebnis {quizCorrect}/{media.length} → {quizCorrect >= (round.targetCount ?? 0) ? 'SUCCESS' : 'FAIL'}
            </p>
          )}
          <Button size="sm" variant="outline" onClick={() => P({ quizIndex: 0, quizCorrect: 0 })} className="border-white/20">Quiz zurücksetzen</Button>
        </div>
      )}

      {type === 'RANDOM_TEAMMATES' && round.targetCount != null && secs.length > 0 && (
        <div className="space-y-1">
          {secs.map((name, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="flex-1">{name}</span>
              <Button data-testid={`answer-right-${i}`} size="sm" onClick={() => { const a = [...answers]; a[i] = true; P({ answers: a }); }} className={`h-7 ${answers[i] === true ? 'bg-green-600' : 'bg-white/10'}`}>RICHTIG</Button>
              <Button data-testid={`answer-wrong-${i}`} size="sm" onClick={() => { const a = [...answers]; a[i] = false; P({ answers: a }); }} className={`h-7 ${answers[i] === false ? 'bg-red-600' : 'bg-white/10'}`}>FALSCH</Button>
            </div>
          ))}
          {answers.filter(a => a !== undefined && a !== null).length >= secs.length && (
            <p data-testid="answers-final" className={`font-bold ${answers.filter(Boolean).length >= round.targetCount ? 'text-green-400' : 'text-red-400'}`}>
              {answers.filter(Boolean).length}/{secs.length} → {answers.filter(Boolean).length >= round.targetCount ? 'SUCCESS' : 'FAIL'}
            </p>
          )}
        </div>
      )}
      {round.hostNotes && <p className="text-[11px] text-rose-200/50 border-t border-white/10 pt-2">📝 {round.hostNotes}</p>}
    </div>
  );
};

// STAGE SIDE -----------------------------------------------------------------
export const ChallengeLiveStage = ({ round, live = {} }) => {
  const isLive = live.roundId === round.id ? live : {};
  const left = useCountdown(isLive.timerEndsAt);
  const type = round.interactionType || 'NONE';
  const media = round.media || [];
  const quizIdx = isLive.quizIndex || 0;
  const hideTimer = type === 'TIMING_CHALLENGE' && round.targetSeconds && !round.timerSeconds; // 24s: challenger must not see time
  return (
    <div className="flex flex-col items-center gap-6" data-testid="challenge-live-stage">
      {left != null && !hideTimer && (
        <div className={`text-[9rem] leading-none font-black font-mono ${left <= 5 ? 'text-red-500' : 'text-amber-300'}`} data-testid="stage-timer">{left}</div>
      )}
      {type === 'HOST_COUNTER' && (
        <div className="text-[8rem] font-black text-green-400" data-testid="stage-counter">{isLive.valid || 0} <span className="text-white/40 text-6xl">/ {round.targetCount}</span></div>
      )}
      {type === 'IMAGE_QUIZ' && quizIdx < media.length && (
        <div className="flex flex-col items-center gap-3" data-testid="stage-quiz">
          <p className="text-2xl text-white/60">Werkzeug {quizIdx + 1} / {media.length}</p>
          <img src={media[quizIdx].imageUrl} alt="" className="max-h-[55vh] max-w-[80vw] rounded-2xl shadow-2xl object-contain bg-black/40" />
          <p className="text-3xl text-green-400 font-bold">Richtig: {isLive.quizCorrect || 0}</p>
          <p className="text-[10px] text-white/30">{media[quizIdx].license} · {media[quizIdx].attribution}</p>
        </div>
      )}
      {type === 'IMAGE_QUIZ' && quizIdx >= media.length && media.length > 0 && (
        <p className="text-6xl font-black text-amber-300" data-testid="stage-quiz-final">{isLive.quizCorrect || 0} / {media.length} richtig</p>
      )}
      {type === 'RANDOM_TEAMMATES' && (isLive.answers || []).length > 0 && (
        <div className="flex gap-6 text-5xl" data-testid="stage-answers">
          {(round.secondaryPlayerNames || []).map((n, i) => <span key={i}>{isLive.answers?.[i] === true ? '✅' : isLive.answers?.[i] === false ? '❌' : '⬜'}</span>)}
        </div>
      )}
      {type === 'TIMING_CHALLENGE' && isLive.stoppedAt && isLive.timerStartedAt && (
        <p className="text-7xl font-black font-mono text-amber-300" data-testid="stage-timing-result">{((isLive.stoppedAt - isLive.timerStartedAt) / 1000).toFixed(2)}s</p>
      )}
    </div>
  );
};
